import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { SectionHeading } from '@/components/generals/section-heading'

interface ServicesShowcaseProps {
    services: Service[]
}

export const ServicesShowcase: FC<ServicesShowcaseProps> = ({ services }): ReactNode => {
    const [active, setActive] = useState(0)

    if (services.length === 0) return null

    return (
        <section id='services' aria-labelledby='services-title' className='surface-night py-24 text-white lg:py-32'>
            <div className='mx-auto max-w-7xl px-6'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        tone='dark'
                        eyebrow='What we do'
                        titleId='services-title'
                        title={(
                            <>
                                Our signature
                                {' '}
                                <em className='text-gold'>services</em>
                            </>
                        )}
                        description='From a family haldi to a stadium on match day, every event gets the same planning, people and polish.'
                    />

                    <ButtonLink href='/services' variant='outline-light' className='self-start lg:self-auto'>
                        All services
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </ButtonLink>
                </div>

                <div className='mt-14 grid gap-12 lg:grid-cols-12'>
                    <ol className='border-t border-white/10 lg:col-span-7'>
                        {services.map((service, index) => (
                            <li key={service.id} className='border-b border-white/10'>
                                <Link
                                    href={`/services#${service.slug}`}
                                    onMouseEnter={() => setActive(index)}
                                    onFocus={() => setActive(index)}
                                    className='focus-ring group grid grid-cols-[auto_1fr_auto] items-center gap-5 rounded-xl py-6 sm:gap-8'
                                >
                                    <span className={cn('text-xs font-semibold tabular-nums transition-colors', active === index ? 'text-gold' : 'text-white/35')}>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>

                                    <span className='min-w-0'>
                                        <span
                                            className={cn(
                                                'block font-heading text-3xl leading-tight transition-colors sm:text-4xl',
                                                active === index ? 'text-white' : 'text-white/70 group-hover:text-white'
                                            )}
                                            data-mdlfy-cms={`services:${service.id}:title`}
                                        >
                                            {service.title}
                                        </span>

                                        {service.tagline && (
                                            <span className='mt-2 block text-sm leading-relaxed text-white/55' data-mdlfy-cms={`services:${service.id}:tagline`}>
                                                {service.tagline}
                                            </span>
                                        )}
                                    </span>

                                    <span className='flex items-center gap-4'>
                                        {service.image && (
                                            <span className='relative block size-16 shrink-0 overflow-hidden rounded-xl lg:hidden'>
                                                <Image src={service.image} alt='' fill sizes='64px' className='object-cover' />
                                            </span>
                                        )}

                                        <ArrowUpRight
                                            className={cn(
                                                'hidden size-6 shrink-0 transition-all duration-300 lg:block',
                                                active === index ? 'translate-x-0.5 -translate-y-0.5 text-gold' : 'text-white/30'
                                            )}
                                            aria-hidden='true'
                                        />
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ol>

                    <div aria-hidden='true' className='hidden lg:col-span-5 lg:block'>
                        <div className='sticky top-28'>
                            <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-white/10 bg-night-soft shadow-2xl shadow-black/50'>
                                {services.map((service, index) => service.image && (
                                    <Image
                                        key={service.id}
                                        src={service.image}
                                        alt=''
                                        fill
                                        sizes='40vw'
                                        className={cn(
                                            'object-cover transition-all duration-700 ease-out',
                                            active === index ? 'scale-100 opacity-100' : 'scale-105 opacity-0'
                                        )}
                                    />
                                ))}

                                <div className='absolute inset-0 bg-linear-to-t from-night/70 via-transparent to-transparent' />

                                <span className='absolute bottom-6 left-6 rounded-full border border-white/20 bg-night/60 px-4 py-2 text-xs font-semibold tracking-[0.18em] text-white uppercase backdrop-blur'>
                                    {String(active + 1).padStart(2, '0')}
                                    {' / '}
                                    {String(services.length).padStart(2, '0')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
